import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "../features.css";
import API from "../../utils/API";
import { FEATURES } from "../../config/features";
import { useAuth } from "../../auth/AuthProvider";
import { tierCan } from "../../auth/permissions";
import { ownSplitFor, useBookData } from "../../data/BookDataProvider";
import {
  COMMISSION_RATE,
  daysUntil,
  formatDate,
  formatMoney,
  parsePremium,
  renewalInfo,
  renewalVariant,
  sumPremiums
} from "../../data/format";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  SkeletonText,
  ServiceIcon,
  ClockIcon,
  useToast
} from "../../ui";
import { LobBadge } from "../shared";
import { srTypeLabel } from "../service/srLabels";

/**
 * Today (§3.6): the agent home — queues with a next action, not a data
 * dump. Renewals next 60 days, open service requests, book at a glance.
 * Tier-aware: a referral agent's numbers are their scoped book with
 * split-weighted commission estimates; staff see whatever their token's
 * lists return (full book for admin; writing-tier read-unscoping arrives
 * with Session H's backend).
 */

const RENEWAL_WINDOW_DAYS = 60;
const FEED_LIMIT = 8;

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) {
    return "Good morning";
  }
  if (hour < 17) {
    return "Good afternoon";
  }
  return "Good evening";
}

function TodayPage() {
  const navigate = useNavigate();
  const { identity, user, tier } = useAuth();
  const can = tierCan(tier);
  const { loading, error, households, requests, refresh } = useBookData();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get("review"); // digest deep-link → Needs review

  // Agent records carry firstName/lastName; Admin records carry `name`.
  const firstName =
    (identity && identity.firstName) ||
    (identity && identity.name && identity.name.split(" ")[0]) ||
    (user && user.email ? user.email.split("@")[0] : "there");

  const renewals = useMemo(() => {
    const rows = [];
    for (const h of households) {
      for (const entry of h.activePolicies) {
        // The feed wants EVERY policy renewing in the window, not just each
        // household's next one.
        const renewal = renewalInfo(entry);
        if (!renewal) {
          continue;
        }
        const days = daysUntil(renewal.date);
        if (days != null && days >= 0 && days <= RENEWAL_WINDOW_DAYS) {
          rows.push({ household: h, policy: entry, renewal, days });
        }
      }
    }
    return rows.sort((a, b) => a.days - b.days);
  }, [households]);

  const openRequests = useMemo(() => {
    const byId = new Map(households.map((h) => [h.id, h]));
    return requests
      .filter((r) => !r.completed && r.active !== false)
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .map((r) => ({
        request: r,
        household: byId.get(String((r.client && r.client._id) || r.client)) || null
      }));
  }, [requests, households]);

  const stats = useMemo(() => {
    const activePolicies = households.flatMap((h) => h.activePolicies);
    // P1 (gate smoke): one garbage premium (a $1.59B home policy) inflated
    // these tiles 640×. Aggregates use the guarded sum and SAY when they
    // exclude something — the raw value stays visible on the policy row.
    const { total: premium, excluded } = sumPremiums(activePolicies);
    let commission;
    if (can.isStaff) {
      // Semantics (Evan's question, labeled below): the staff figure is the
      // GROSS book estimate — 2.5% of premium in force, BEFORE referral
      // splits are paid out.
      commission = premium * COMMISSION_RATE;
    } else {
      commission = activePolicies.reduce((sum, p) => {
        const split = ownSplitFor(p, identity);
        return split == null
          ? sum
          : sum + parsePremium(p.premium) * COMMISSION_RATE * (split / 100);
      }, 0);
    }
    return {
      households: households.length,
      policies: activePolicies.length,
      premium,
      commission,
      excluded: excluded.length
    };
  }, [households, can.isStaff, identity]);

  const today = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric"
  }).format(new Date());

  if (error) {
    return (
      <div className="shell-page">
        <div className="shell-page__titlerow">
          <h1 className="shell-page__title">Today</h1>
        </div>
        <Card>
          <EmptyState
            title="Something went wrong"
            message={error}
            action={
              <Button variant="primary" onClick={refresh}>
                Try again
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="shell-page">
      <div className="shell-page__titlerow">
        <h1 className="shell-page__title">
          {greeting()}, {firstName}
        </h1>
        <span className="feat-cell-secondary">{today}</span>
      </div>

      <div className="feat-stats" style={{ marginBottom: "var(--space-5)" }}>
        <div className="feat-stat">
          <span className="feat-stat__label">Households</span>
          <span className="feat-stat__value">{loading ? "…" : stats.households}</span>
        </div>
        <div className="feat-stat">
          <span className="feat-stat__label">Active policies</span>
          <span className="feat-stat__value">{loading ? "…" : stats.policies}</span>
        </div>
        <div className="feat-stat">
          <span className="feat-stat__label">Premium in force</span>
          <span className="feat-stat__value">{loading ? "…" : formatMoney(stats.premium)}</span>
          {!loading && stats.excluded > 0 ? (
            <span className="feat-stat__hint">
              {stats.excluded} polic{stats.excluded === 1 ? "y" : "ies"} excluded — implausible
              premium on file, needs a data fix
            </span>
          ) : null}
        </div>
        <div className="feat-stat">
          <span className="feat-stat__label">
            {can.isStaff ? "Est. gross commission" : "Your est. commission"}
          </span>
          <span className="feat-stat__value">{loading ? "…" : formatMoney(stats.commission)}</span>
          <span className="feat-stat__hint">
            {can.isStaff
              ? "2.5% of premium in force, before referral splits"
              : "2.5% × your splits, annualized"}
          </span>
        </div>
      </div>

      <div className="feat-today-grid">
        <Card title={`Renewals — next ${RENEWAL_WINDOW_DAYS} days`}>
          {loading ? (
            <SkeletonText lines={4} />
          ) : renewals.length === 0 ? (
            <EmptyState
              icon={<ClockIcon size={32} />}
              title="Nothing renewing soon"
              message={`No policies renew in the next ${RENEWAL_WINDOW_DAYS} days.`}
            />
          ) : (
            <div className="feat-feed">
              {renewals.slice(0, FEED_LIMIT).map(({ household, policy, renewal, days }) => (
                <button
                  key={policy._id}
                  type="button"
                  className="feat-feed__row"
                  onClick={() => navigate(`/app/clients/${household.id}?tab=policies`)}
                >
                  <Badge variant={renewalVariant(days)}>
                    {days === 0 ? "today" : `${days}d`}
                    {renewal.estimated ? " · est." : ""}
                  </Badge>
                  <span className="feat-feed__main">
                    <span className="feat-feed__title">{household.name}</span>
                    <span className="feat-feed__meta">
                      {policy.lob} · {policy.carrier || "Unknown carrier"} · renews{" "}
                      {formatDate(renewal.date)}
                    </span>
                  </span>
                  <LobBadge lobKey={policy.lobKey} lob={policy.lob} />
                </button>
              ))}
              {renewals.length > FEED_LIMIT ? (
                <Link className="feat-feed__more" to="/app/book?renews=60">
                  View all {renewals.length} in the Book →
                </Link>
              ) : null}
            </div>
          )}
        </Card>

        <div style={{ display: "grid", gap: "var(--space-5)" }}>
          <Card title={`Open service requests${loading ? "" : ` (${openRequests.length})`}`}>
            {loading ? (
              <SkeletonText lines={3} />
            ) : openRequests.length === 0 ? (
              <EmptyState
                icon={<ServiceIcon size={32} />}
                title="No open requests"
                message="Nothing waiting on service right now."
              />
            ) : (
              <div className="feat-feed">
                {openRequests.slice(0, FEED_LIMIT).map(({ request, household }) => (
                  <button
                    key={request._id}
                    type="button"
                    className="feat-feed__row"
                    onClick={() =>
                      household ? navigate(`/app/clients/${household.id}?tab=service`) : undefined
                    }
                    style={household ? undefined : { cursor: "default" }}
                  >
                    <Badge variant="warning" dot>
                      Open
                    </Badge>
                    <span className="feat-feed__main">
                      <span className="feat-feed__title">
                        {household ? household.name : request.name || "Unknown client"}
                      </span>
                      <span className="feat-feed__meta">
                        {[srTypeLabel(request.type), formatDate(request.created_at)]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </Card>

          <ReachOutQueue />

          <NeedsReviewQueue targetId={reviewId} />

          <DataFixesCard />

          <Card title="Book of Business">
            <p style={{ margin: "0 0 var(--space-3)" }} className="feat-cell-secondary">
              Search and filter every household, and open any client hub.
            </p>
            <Link to="/app/book">
              <Button variant="secondary">Open the Book →</Button>
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
}

/**
 * W12 — the reach-out queue, on the SHIPPED contract (H Tranche 4):
 * GET /api/nudges/reach-outs (writing tier), entries carrying the populated
 * prospect + asking agent. Behind FEATURES.nudges; absent (not empty) when
 * off or when there's nothing. The "Mark contacted" close-out is behind
 * FEATURES.nudgeContacted (PATCH /api/nudges/:id/contacted) — when that flag
 * is off the queue is display-only, its prior shipped behavior.
 */
function ReachOutQueue() {
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!FEATURES.nudges) {
      return undefined;
    }
    let cancelled = false;
    API.getReachOuts()
      .then((res) => {
        if (!cancelled) {
          setItems(Array.isArray(res.data) ? res.data : []);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const markContacted = async (id) => {
    // Optimistic: the outreach is done, drop it from the queue immediately.
    setItems((prev) => prev.filter((n) => n._id !== id));
    try {
      await API.markNudgeContacted(id);
    } catch (err) {
      // 409 = already contacted — the outcome we wanted, treat as success.
      if (err && err.response && err.response.status === 409) {
        return;
      }
      toast.error("Couldn't mark that contacted — try again.");
      // Restore from the server so nothing is silently dropped.
      API.getReachOuts()
        .then((res) => setItems(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    }
  };

  if (!FEATURES.nudges || items.length === 0) {
    return null;
  }
  return (
    <Card title={`Reach out (${items.length})`}>
      <div className="feat-feed">
        {items.map((n) => {
          const prospectId =
            n.prospectRef &&
            (n.prospectRef._id || (typeof n.prospectRef === "string" && n.prospectRef));
          return (
            /* H-2 (E's QA): the row names WHO to call and WHO asked, and
               links to the prospect (the digest's pinned ?prospect= URL) —
               a reach-out you can act on, not an anonymous line. Shipped
               shape: population happens IN PLACE on prospectRef/agentRef. */
            <div key={n._id} className="feat-feed__row feat-feed__row--split">
              <button
                type="button"
                className="feat-feed__navbtn"
                onClick={
                  prospectId ? () => navigate(`/app/pipeline?prospect=${prospectId}`) : undefined
                }
                style={prospectId ? undefined : { cursor: "default" }}
              >
                <Badge variant="brand">☎</Badge>
                <span className="feat-feed__main">
                  <span className="feat-feed__title">
                    {(n.prospectRef && n.prospectRef.name) || n.prospectName || "A past referral"}
                  </span>
                  <span className="feat-feed__meta">
                    asked by{" "}
                    {(n.agentRef &&
                      [n.agentRef.firstName, n.agentRef.lastName].filter(Boolean).join(" ")) ||
                      "their referring agent"}
                    {n.note ? ` · “${n.note}”` : ""}
                  </span>
                </span>
              </button>
              {FEATURES.nudgeContacted ? (
                <Button size="sm" variant="ghost" onClick={() => markContacted(n._id)}>
                  Mark contacted
                </Button>
              ) : null}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/**
 * W10 — Sentinel needsReview, simple-list pass. Ratified contract:
 * GET /api/activities/review (writing/admin only), behind the activity
 * flag family. Absent when off/empty.
 */
function NeedsReviewQueue({ targetId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!FEATURES.activityFeed) {
      return undefined;
    }
    let cancelled = false;
    API.getActivitiesReview()
      .then((res) => {
        if (!cancelled) {
          setItems(Array.isArray(res.data) ? res.data : []);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Digest deep-link (?review=<id>): scroll to and pulse the flagged item once
  // the list loads. Inert until the digest links here (worker T6).
  useEffect(() => {
    if (!targetId || items.length === 0) {
      return undefined;
    }
    const t = window.setTimeout(() => {
      const el = document.querySelector(".feat-target-pulse");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
    return () => window.clearTimeout(t);
  }, [targetId, items]);

  if (!FEATURES.activityFeed || items.length === 0) {
    return null;
  }
  return (
    <Card title={`Needs review (${items.length})`}>
      <div className="feat-feed">
        {items.map((a) => (
          <div
            key={a._id}
            className={`feat-feed__row${a._id === targetId ? " feat-target-pulse" : ""}`}
            style={{ cursor: "default" }}
          >
            <Badge variant="danger">Review</Badge>
            <span className="feat-feed__main">
              <span className="feat-feed__title" style={{ whiteSpace: "normal" }}>
                {a.body}
              </span>
              <span className="feat-feed__meta">
                {[formatDate(a.created_at), a.source].filter(Boolean).join(" · ")}
              </span>
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * The Today "Data fixes (N)" count card (§B1) — same pattern as Needs review.
 * Behind FEATURES.dataFixes; absent when zero (absent-not-empty).
 */
function DataFixesCard() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!FEATURES.dataFixes) {
      return undefined;
    }
    let cancelled = false;
    API.getCustodianQueueCount()
      .then((res) => {
        if (!cancelled) {
          setCount((res.data && res.data.open) || 0);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!FEATURES.dataFixes || count === 0) {
    return null;
  }
  return (
    <Card title={`Data fixes (${count})`}>
      <p style={{ margin: "0 0 var(--space-3)" }} className="feat-cell-secondary">
        {count} {count === 1 ? "finding" : "findings"} from the Custodian's latest sweep.
      </p>
      <Button variant="secondary" onClick={() => navigate("/app/datafixes")}>
        Review →
      </Button>
    </Card>
  );
}

export default TodayPage;
